import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { TslDialog } from "@tesla/ng-tds";
import { forkJoin, Observable, of } from "rxjs";
import { ESignService } from "../../../profile/services/esign.service";
import { OnboardingDocumentsService } from "../../../profile/services/onboarding-document.service";
import { OnboardingDocument } from "../../../profile/models/onboarding.model";
import { DocumentsDialogComponent } from "../documents-dialog/documents-dialog.component";
import { isNullOrEmpty, validateAllFormGroupsInArrayForm, validateESignDocument } from "../../../shared/utils/utils";
import * as FileSaver from 'file-saver';
import { X } from "@angular/cdk/keycodes";
import { I18nService } from "app/shared/services/i18n.service";

/** Reusable grid for uploading documents */
@Component({
    selector: 'documents-grid',
    templateUrl: './documents-grid.component.html',
    styleUrls: ['./documents-grid.component.scss']
})
export class DocumentsGridComponent implements OnInit, OnChanges {
    /** Form array of the documents the user needs to upload or sign */
    documentsFormArray: FormArray;
    optionalListDocumentFormArray: FormArray;

    /** Required supplier code to be provided by onboaridng parent component */
    @Input('tsl-supplierCode') supplierCode: string = '';

    /** Flag to disable validations for specific conditions */
    @Input('tsl-disableValidations') disableValidations: boolean = false;

    /** Documents that the user needs to upload or has uploaded */
    @Input('tsl-documents') documents: OnboardingDocument[] = [];

    /** Documents that the user needs to upload or has uploaded */
    @Input('tsl-optional-documents') optionalDocuments: OnboardingDocument[] = [];

    /** Determines if the controls should be disabled */
    @Input('tsl-readOnly') readOnly = false;


    @Input('tsl-isOnboarding') isOnboarding = false;

    @Input('tsl-isReactivation') isReactivation = false;

    constructor(
        private formBuilder: FormBuilder,
        private matDialog: MatDialog,
        private tslDialog: TslDialog,
        private onboardingDocumentsService: OnboardingDocumentsService,
        private esignService: ESignService,
        private translate: I18nService,
    ) {
    }

    ngOnInit(): void {
        //  Initialize documents form array
        this.setDocumentsFormArray();
        this.setOptionalDocumentsFormArray();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.documents) {
            //  Reset form array when documents input property changes
            console.log('Docs documents', this.documents);
            this.setDocumentsFormArray();
        }

        if(changes.optionalDocuments){
            console.log('Optional documents', this.optionalDocuments);
           this.setOptionalDocumentsFormArray();
        }
    }

    onDocumentTypeChange(index: number, documentType: string) 
    {
        console.log('Document type changed', index, documentType);
        var currentFormControlForm = this.optionalListDocumentFormArray.at(index) as FormGroup;
        var doc = this.optionalDocuments[index];
        var docInfo = doc.DocumentTypeList?.filter(x => x.DocumentType == documentType)[0];

        if(docInfo)
        {
            currentFormControlForm.patchValue({
                DocumentType: documentType,
                DocumentTypeDescription: docInfo?.DocumentTypeDescription,
                TypeID: docInfo?.DocumentTypeID
            }, { emitEvent: false });
    
            doc.DocumentType = documentType;
            doc.DocumentTypeDescription = docInfo?.DocumentTypeDescription; 
            doc.TypeID = docInfo?.DocumentTypeID.toString();  
        }
    }

    /** Determines if the document form is valid */
    public isValid(): Observable<boolean> {
        let result = this.documentsFormArray.valid;
        if(this.optionalDocuments?.length > 0){
            result = this.optionalListDocumentFormArray.valid && result
        }

        if (!result) {
            validateAllFormGroupsInArrayForm(this.documentsFormArray);
            
            if(this.optionalDocuments?.length > 0){
                validateAllFormGroupsInArrayForm(this.optionalListDocumentFormArray);
            }
        }

        // If no basic validation failures check for eSign validations
        if (result === true) {
            //  Validate E-Sign Documents
            return this.validateAllESignDocuments();
        }else{
            return of(result);
        }
    }

    /** Instanciate documents form array with a group of controls for each document in the input property */
    public setDocumentsFormArray() {
        this.documentsFormArray = this.formBuilder.array(
            this.documents.map(doc => {
                let formGroup = this.createFormBuilder(doc);
                doc.FormGroup = formGroup;
                return formGroup;
            })
        );

        this.updateESignStatus();
    }

    public setOptionalDocumentsFormArray()
    {
        this.optionalListDocumentFormArray = this.formBuilder.array(
            this.optionalDocuments.map(doc => {
                let formGroup = this.createFormBuilder(doc);
                doc.FormGroup = formGroup;
                return formGroup;
            })
        );
    }

    public createFormBuilder(doc: OnboardingDocument): FormGroup
    {
        return this.formBuilder.group({
            DocumentType: [doc?.DocumentType, Validators.required],
            DocumentTypeDescription: [doc?.DocumentTypeDescription],
            TypeID: [doc?.TypeID],
            FileName: [doc?.FileName, (doc?.ValidationRequired == false && this.isReactivation) ? null : this.isNotNullOrUndefined(doc.Templates) && doc?.Templates?.length > 0 && this.IsEsignRequired(doc) === true ? null : this.GetFormValidations(doc)],
            FileNameGuid: [doc?.FileNameGuid, ((doc?.ValidationRequired == false && this.isReactivation) || doc?.IsOptionalList == true) ? null : this.isNotNullOrUndefined(doc.Templates) && doc?.Templates?.length > 0 && this.IsEsignRequired(doc) === true ? null : this.GetFormValidations(doc)],
            UploadedDate: [doc?.UploadedDate, (doc?.ValidationRequired == false && this.isReactivation) ? null : this.isNotNullOrUndefined(doc.Templates) && doc?.Templates?.length > 0 && this.IsEsignRequired(doc) === true ? null : this.GetFormValidations(doc)],
            SignedDate: [doc?.SignedDate ? ((doc?.ValidationRequired == false && this.isReactivation)) ? null : this.getFormattedDate(doc.SignedDate) : doc.SignedDate, doc.SignDateRequired === true || this.isNotNullOrUndefined(doc.SignedDate) ? this.GetFormValidations(doc) : null],
            DocumentID: [doc?.DocumentID, (doc?.ValidationRequired == false && this.isReactivation) ? null : this.isNotNullOrUndefined(doc.Templates) && doc?.Templates?.length > 0 && this.IsEsignRequired(doc) === true ? null : this.GetFormValidations(doc)],
            ESignAgreementId: [doc?.ESignAgreementId, this.isNotNullOrUndefined(doc.Templates) && doc.Templates.length > 0 && this.IsEsignRequired(doc) === true ?
                this.GetFormValidations(doc) : null],
            DocumentVersion: [doc.DocumentVersion, (doc?.ValidationRequired == false && this.isReactivation) ? null : this.isNotNullOrUndefined(doc.Templates) && doc?.Templates?.length > 0 && this.IsEsignRequired(doc) === true ? null : this.GetFormValidations(doc)],
        });
    }

    /** Determines if the controls needs any validation */
    GetFormValidations(doc: OnboardingDocument){
        if(this.disableValidations === true || doc.IsOptional == true){
            return null;
        }
        return Validators.required;
    }

    /** Determines if the document type has a mode of esign */
    public IsEsignRequired(item: OnboardingDocument) {
        if (item && item.Templates && item.Templates.length > 0) {
            return item.Mode.toLowerCase() === "esign";
        }
        return false;
    }

    /** Determiens if user can eSign document by checking the ESingAgreementId or the Agreement status */
    public IsEsignAllowed(item: OnboardingDocument) {
        if (item && item.Templates && item.Templates.length > 0) {
            if (item.Mode.toLowerCase() === "esign") {
                let agreementStatus = item.ESignAgreementStatus;

                // If esign agreement is not generated yet or
                // agreement status is expired ie. agreement is not esigned by supplier in 90 days.
                // Allow supplier to generate esign
                if (!item.ESignAgreementId || (agreementStatus && "EXPIRED" === agreementStatus.toUpperCase())) {
                    return true;
                }
            }
        }
        return false;
    }

    /** Determines if we can display the upload button for the current document item */
    public IsUploadAllowed(item: OnboardingDocument) {

        // Upload should be allowed only if either document type mode is upload in onboarding information or
        // There is no template available for the document type.
        if (item) {
            return item.Mode.toLowerCase() === "upload" || (item.Templates && item.Templates.length == 0);
        }
        return false;
    }

    /** Determines if we can download the template of a document */
    public IsDownloadTemplateAvailable(item: OnboardingDocument) {

        // Download template button should be available only if document type mode is upload and it has template.
        if (item && item.Templates && item.Templates.length > 0) {
            return item.Mode.toLowerCase() === "upload";
        }
        return false;
    }

    /** Uploads document */
    public uploadFilePopUp(item: OnboardingDocument) {

        //  Open Dialog Component to upload files, pass a reference to the onboarding document item
        //  The dialog component will update the document item fields directly
        let dialogRef = this.matDialog.open(DocumentsDialogComponent, {
            width: '600px',
            data: { documentData: item, supplierCode: this.supplierCode }
        });

        //  Reset document form array since the documents array data has changed.
        dialogRef.afterClosed().subscribe(() => {
            if (item.DocumentID) {
                //  Reset documents form
                this.setDocumentsFormArray();
            }
        });
    }

    public uploadOptionalFilePopUp(item: OnboardingDocument) {

        //  Open Dialog Component to upload files, pass a reference to the onboarding document item
        //  The dialog component will update the document item fields directly
        let dialogRef = this.matDialog.open(DocumentsDialogComponent, {
            width: '600px',
            data: { documentData: item, supplierCode: this.supplierCode }
        });

        //  Reset document form array since the documents array data has changed.
        dialogRef.afterClosed().subscribe(() => {
            if (item.DocumentID) {
                //  Reset documents form
                this.setOptionalDocumentsFormArray();
            }
        });
    }

    /** Downloads file document */
    public downloadDocument(document: OnboardingDocument, isTemplate: boolean = false) {
        if(document && this.isNotNullOrUndefined(document.File)){
            FileSaver.saveAs(document.File, document.FileName || document.DocumentID);
        }else{
            if (document && document.DocumentID) {
                this.onboardingDocumentsService.downloadDocument(document.DocumentID, this.supplierCode, document.DocumentVersion).subscribe(resultBlob => {
                    FileSaver.saveAs(resultBlob, document.FileName || document.DocumentID);
                });
            }
        }
    }

    /** Downloads template of a document */
    public downloadTemplate(document: OnboardingDocument, isTemplate: boolean = true) {
        let templates = document.Templates;
        if (templates && templates.length >= 1) {
            this.onboardingDocumentsService.downloadTemplate(document.Templates[0], this.supplierCode, document.DocumentVersion).subscribe(resultBlob => {
                FileSaver.saveAs(resultBlob, document.FileName || `${document.DocumentTypeDescription}.pdf`);
            });;
        }
    }

    /** Removes data of the document at index */
    public removeDocument(index: number, isOptional: boolean = false) {
        var docFormGroup = (isOptional == false) ? this.documentsFormArray.at(index) as FormGroup : this.optionalListDocumentFormArray.at(index) as FormGroup;
        docFormGroup.patchValue({
            DocumentID: null,
            FileName: null,
            FileNameGuid: null,
            UploadedDate: null,
            DocumentVersion: null
        });
        let doc = (isOptional == false) ? this.documents[index] : this.optionalDocuments[index];
        doc = {
            ...doc,
            DocumentID: null,
            FileName: null,
            FileNameGuid: null,
            UploadedDate: null,
            DocumentVersion: null
        };

        if(isOptional == false){
            this.documents[index] = doc;
        }
        else
        {
            this.optionalDocuments[index] = doc;
        }
    }

    public publishForESign(item: OnboardingDocument, templateId: string) {

        let typeId = <number><unknown>item.TypeID;
        this.esignService.publishForESign(this.supplierCode, templateId, typeId).mergeMap(resp => {
            item.ESignAgreementId = resp.agreementId;
            return this.esignService.getESignDocumentStatus(resp.agreementId);
        }).subscribe(status => {
            item.ESignAgreementStatus = status;

            //  Open document to sign
            this.openEsignDocument(item.ESignAgreementId);

            // reset document form array
            this.setDocumentsFormArray();
        });
    }

    public openEsignDocument(agreementId: string) {
        this.esignService.getESignDocumentStatus(agreementId).subscribe(documentStatus => {

            if (validateESignDocument(documentStatus)) {
                this.tslDialog.alert('Validation', 'E-Sign is complete for this document');
            } else {
                this.esignService.getEsignUrl(agreementId).subscribe(urlResp => {
                    if (urlResp) {
                        window.open(urlResp.esignUrl, "_blank");
                    }
                });
            }
        });
    }

    /** Formats date to MM/dd/yyyy */
    private getFormattedDate(dateInput: Date | string) {
        let date = dateInput instanceof Date ? dateInput : new Date(dateInput);
        let year = date.getFullYear();

        let month = (1 + date.getMonth()).toString();
        month = month.length > 1 ? month : '0' + month;

        let day = date.getDate().toString();
        day = day.length > 1 ? day : '0' + day;

        return month + '/' + day + '/' + year;
    }

    /** Check if field control has been touched and if it's valid */
    public isFieldInvalid(formGroup: FormGroup, field: string): boolean {
        if (formGroup) {
            return !formGroup.get(field).valid && formGroup.get(field).touched;
        } else {
            return false;
        }
    }

    /** Utility */
    public isNotNullOrUndefined(val: any): boolean {
        return  !(val == 'undefined' || val == null);
    }

    /** Updates the agreement status of all the documents that require esign and have an agreement ID */
    private updateESignStatus(): void {
        //  Get documents that require an E-Sign and have an agreemnet id
        let esignDocuments = this.documents.filter(doc => this.IsEsignRequired(doc) && doc.ESignAgreementId != null && doc.ESignAgreementId != undefined);

        //  Send request for each document to get status and update the agreement status
        esignDocuments.forEach(document => {
            let sub = this.esignService.getESignDocumentStatus(document.ESignAgreementId)
                .map(status => {
                    //  Update ESign Status of the document before we get the status
                    document.ESignAgreementStatus = status;
                    return status;
                })
                .subscribe();
        });

    }

    /** Gets user friendly validation error messages */
    public getValidationMessages(displayName: string, control: FormControl) {
        if (control.dirty || control.touched) {
            if (control.hasError('required')) {
                return `${displayName} is required`;
            }
            if (control.hasError('minlength') || control.hasError('maxlength')) {
                return `Size of the input is invalid.`;
            } else if (control.hasError('pattern')) {
                return `${displayName} contains invalid characters`;
            }
        }
    }

    /** Checks that the user has E-Signed all documents that require an E-Signature */
    private validateAllESignDocuments(): Observable<boolean> {
        //  Get the Agreement IDs of the documents that require an E-Sign
        let esignDocuments = this.documents.filter(doc => this.IsEsignRequired(doc));

        //  Check if we have esign document
        if (!esignDocuments || esignDocuments.length == 0) {
            //  There's no esign documents to check
            return of(true);
        }

        //  Every document should have an agreement ID
        if (esignDocuments.some(doc => doc.ESignAgreementId === null || doc.ESignAgreementId === undefined)) {
            return of(false);
        }

        //  Create an array of observables for getting the status of each agreement and wait for all of them to complete
        //  Once we get the status of all the documents, then check if every status is valid
        let getStatusTasks = esignDocuments.map(document =>
            this.esignService.getESignDocumentStatus(document.ESignAgreementId)
                .map(status => {
                    //  Update ESign Status of the document before we get the status
                    document.ESignAgreementStatus = status;
                    return status;
                })
        );
        return forkJoin(getStatusTasks).map(statuses => statuses.every(status => validateESignDocument(status)));
    }
}